
import { toast } from 'sonner';
import { useTimetableStore } from '../store/timetableStore';
import { ClassItem } from '@/types/timetable';

interface TimetableActionProps {
  createClass: (teacherId: string, classData: Partial<ClassItem>) => Promise<boolean>;
  updateClass: (teacherId: string, classId: string, classData: Partial<ClassItem>) => Promise<boolean>;
  deleteClass: (teacherId: string, classId: string) => Promise<boolean>;
  updateClassStatus: (teacherId: string, classId: string, newStatus: 'present' | 'late' | 'absent' | { status: 'present' | 'late' | 'absent' }) => Promise<boolean>;
}

export const useTimetableActions = (): TimetableActionProps => {
  const { 
    createClass, 
    updateClass, 
    deleteClass, 
    updateClassStatus 
  } = useTimetableStore();

  const handleCreateClass = async (teacherId: string, classData: Partial<ClassItem>): Promise<boolean> => {
    try {
      console.log('Creating class with full data:', teacherId, classData);
      await createClass(teacherId, classData);
      toast.success('Class created successfully');
      return true;
    } catch (error) {
      console.error('Failed to create class:', error);
      toast.error('Failed to create class');
      throw error;
    }
  };

  const handleUpdateClass = async (teacherId: string, classId: string, classData: Partial<ClassItem>): Promise<boolean> => {
    try {
      console.log('Updating class with full data:', teacherId, classId, classData);
      await updateClass(teacherId, classId, classData);
      toast.success('Class updated successfully');
      return true;
    } catch (error) {
      console.error('Failed to update class:', error);
      toast.error('Failed to update class');
      throw error;
    }
  };

  const handleDeleteClass = async (teacherId: string, classId: string): Promise<boolean> => {
    try {
      console.log('Deleting class:', teacherId, classId);
      await deleteClass(teacherId, classId);
      toast.success('Class deleted successfully');
      return true;
    } catch (error) {
      console.error('Failed to delete class:', error);
      toast.error('Failed to delete class');
      throw error;
    }
  };

  const handleUpdateClassStatus = async (
    teacherId: string, 
    classId: string, 
    newStatus: 'present' | 'late' | 'absent' | { status: 'present' | 'late' | 'absent' }
  ): Promise<boolean> => {
    try {
      const statusValue = typeof newStatus === 'object' && newStatus !== null 
        ? newStatus.status 
        : newStatus;
      
      console.log('Updating class status with value:', teacherId, classId, statusValue);
      await updateClassStatus(teacherId, classId, statusValue as 'present' | 'late' | 'absent');
      toast.success('Status updated successfully');
      return true;
    } catch (error) {
      console.error('Failed to update class status:', error);
      toast.error('Failed to update status');
      throw error;
    }
  };

  return {
    createClass: handleCreateClass,
    updateClass: handleUpdateClass,
    deleteClass: handleDeleteClass,
    updateClassStatus: handleUpdateClassStatus,
  };
};
